import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
} from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import {
  HoldingViewModel,
  useUpdateHoldingMutation,
} from '@/store/holdingsApi';

interface EditCostBasisDialogProps {
  open: boolean;
  onClose: () => void;
  holding: HoldingViewModel;
}

const EditCostBasisDialog: React.FC<EditCostBasisDialogProps> = ({
  open,
  onClose,
  holding,
}) => {
  const { t } = useLocalization();
  const [costBasis, setCostBasis] = useState('0.00');
  const [updateHolding, { isLoading }] = useUpdateHoldingMutation();

  useEffect(() => {
    if (open) {
      setCostBasis(
        holding.CostBasis !== undefined && holding.CostBasis !== null
          ? holding.CostBasis.toFixed(2)
          : '0.00',
      );
    }
  }, [open, holding]);

  const handleSave = async () => {
    const parsed = parseFloat(costBasis);
    if (isNaN(parsed) || parsed < 0) return;

    // Close the dialog immediately for instant user feedback
    onClose();

    try {
      const success = await updateHolding({
        HoldingId: holding.HoldingId,
        CostBasis: parsed,
      }).unwrap();
      if (!success) {
        console.error('Update holding returned false');
      }
    } catch (err) {
      console.error('Failed to update cost basis', err);
      // The optimistic update rollback will handle reverting the UI if the request fails
      // Note: Dialog is already closed, but the flyout will show the reverted value
    }
  };

  const handleCostBasisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow empty input
    if (value === '') {
      setCostBasis('');
      return;
    }

    // Validate decimal input with up to 2 decimal places
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      setCostBasis(value);
    }
  };

  const handleBlur = () => {
    // Format to 2 decimal places on blur if there's a valid number
    if (costBasis && !isNaN(parseFloat(costBasis))) {
      setCostBasis(parseFloat(costBasis).toFixed(2));
    }
  };

  const isValidInput =
    costBasis !== '' &&
    !isNaN(parseFloat(costBasis)) &&
    parseFloat(costBasis) >= 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle>{t('holdingsCostBasis.changeCostBasis')}</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          margin='dense'
          label={t('grid.costBasis')}
          type='text'
          fullWidth
          value={costBasis}
          onChange={handleCostBasisChange}
          onBlur={handleBlur}
          InputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
            style: { textAlign: 'right' },
          }}
          helperText={t('holdingsCostBasis.costBasisHelperText')}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color='inherit'
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={isLoading || !isValidInput}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCostBasisDialog;
